import { useQueries, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import {
  AutoCoderFramework,
  AutoCoderPipeline,
  RecordsQueryRequest,
  RecordsQueryResponse,
} from '../types/gotypes'
import { extractErrorMessage } from '../utils/apitools'
import { recordsHaveInFlightCodings } from '../utils/recordCodings'

/**
 * The one records reader behind every door of the shared records view
 * (`POST /autocoder/:customer/:job/records/query`, T3): a receipt's records, an
 * entity's records, a pipeline's results, a coding batch's members.
 *
 * The filters AND together server-side and every id is scope-checked against
 * the path, so a door is exactly one field. Records come back with ALL of their
 * codings hydrated — the richest version — which is why this endpoint and not
 * the pipeline-scoped readers is what the explorer uses.
 */
export type RecordsExplorerDoor =
  | { importBatchId: string }
  | { entityId: string }
  | { pipelineId: string }
  | { codingBatchId: string }

interface UseRecordsQueryOptions {
  customer: string
  job: string
  door: RecordsExplorerDoor
  /** Filters codings by state when set (server-side). */
  state?: string
  page: number
  rowsPerPage: number
  enabled?: boolean
}

export interface UseRecordsQueryResult {
  data: RecordsQueryResponse | undefined
  loading: boolean
  isFetching: boolean
  error: string | null
  refetch: () => void
}

/** The one non-empty door field, so the query key changes with the door. */
const doorKey = (door: RecordsExplorerDoor): string =>
  Object.entries(door)
    .filter(([, v]) => Boolean(v))
    .map(([k, v]) => `${k}:${v}`)
    .join('|')

export const useRecordsQuery = ({
  customer,
  job,
  door,
  state,
  page,
  rowsPerPage,
  enabled = true,
}: UseRecordsQueryOptions): UseRecordsQueryResult => {
  const key = doorKey(door)

  const query = useQuery<RecordsQueryResponse, Error>({
    queryKey: ['autocoder', customer, job, 'records', { key, state, page, rowsPerPage }],
    queryFn: async () => {
      const body: RecordsQueryRequest = {
        ...door,
        state,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      }
      try {
        const response = await axios.post<RecordsQueryResponse>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/records/query`,
          body,
        )
        return response.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    enabled: enabled && !!customer && !!job && !!key,
    // A page of settled records is a historical fact. It only polls while a
    // coding on THIS page is still moving, and stops the moment the last one
    // lands.
    refetchInterval: query => (
      recordsHaveInFlightCodings(query.state.data?.records || []) ? 3000 : false
    ),
    refetchIntervalInBackground: false,
    staleTime: 10 * 1000,
  })

  return {
    data: query.data,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? extractErrorMessage(query.error) : null,
    refetch: () => {
      void query.refetch()
    },
  }
}

/**
 * The frameworks each stacked coding is read through, fetched ONCE per distinct
 * pipeline on the page (react-query dedupes and caches them, so paging through
 * records coded by the same two pipelines costs two requests in total).
 *
 * A pipeline's frameworks live on the pipeline row's config — there is no
 * per-record framework — so this reads the pipeline the coding names.
 */
export const usePipelineFrameworksMap = (
  customer: string,
  job: string,
  pipelineIds: string[],
): Record<string, AutoCoderFramework[]> => {
  const results = useQueries({
    queries: pipelineIds.map(id => ({
      queryKey: ['autocoder', customer, job, 'pipelineFrameworks', id],
      queryFn: async (): Promise<AutoCoderFramework[]> => {
        try {
          const res = await axios.get<AutoCoderPipeline>(
            `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${id}`,
          )
          return res.data?.config?.frameworks || []
        } catch (error) {
          throw new Error(extractErrorMessage(error))
        }
      },
      enabled: !!customer && !!job && !!id,
      // A framework changes when someone edits the pipeline, not while a page
      // of records is open.
      staleTime: 5 * 60 * 1000,
    })),
  })

  // Built per render rather than memoized: it is a handful of entries, and its
  // only consumer reads it straight through to the row renderer.
  const map: Record<string, AutoCoderFramework[]> = {}
  pipelineIds.forEach((id, index) => {
    const data = results[index]?.data
    if (data) map[id] = data
  })
  return map
}
